import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { getToken } from '../utils/token';
import api from '../api';
import CowAvatar from './CowAvatar';
import NotificationBell from './NotificationBell';
import { pushModal, removeModal } from '../utils/modalStack';

export default function Header() {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(localStorage.getItem('theme') === 'dark');
  const [userName, setUserName] = useState(localStorage.getItem('userName') || 'Usuario');
  const [showJoin, setShowJoin] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const joinRef = useRef(null);
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const avatarSeed = localStorage.getItem('avatarSeed') || userId;
  const isLoggedIn = !!getToken();

  useEffect(() => {
    if (!isLoggedIn) return;
    api.get('/users/me').then(r => setIsAdmin(!!r.data.admin)).catch(() => {});
  }, [isLoggedIn]);

  const closeJoin = () => {
    removeModal(closeJoin);
    setShowJoin(false);
    setJoinCode('');
  };

  const openJoin = () => {
    setShowJoin(prev => {
      if (!prev) pushModal(closeJoin);
      else removeModal(closeJoin);
      return !prev;
    });
    setJoinCode('');
  };

  const handleJoin = () => {
    if (joinCode.trim()) {
      navigate(`/vaquita/preview/${joinCode.trim()}`);
      closeJoin();
    }
  };

  useEffect(() => {
    if (!showJoin) return;
    const handle = (e) => {
      if (joinRef.current && !joinRef.current.contains(e.target)) closeJoin();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showJoin]);

  useEffect(() => {
    const interval = setInterval(() => {
      setUserName(localStorage.getItem('userName') || 'Usuario');
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Aplicar el tema inicial
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, []);

  const toggleTheme = () => {
    const newIsDarkMode = !isDarkMode;
    setIsDarkMode(newIsDarkMode);
    localStorage.setItem('theme', newIsDarkMode ? 'dark' : 'light');
    if (newIsDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    navigate('/');
  };

  return (
    <header className="header">
      <Link to="/home" className="logo">
        🐮 VaquitApp
      </Link>

      {isLoggedIn && (
        <nav className="desktop-nav">
          <NavLink to="/home" className={({ isActive }) => `desktop-nav-link ${isActive ? 'active' : ''}`}>🏠 Inicio</NavLink>
          <div className="join-dropdown-wrap" ref={joinRef}>
            <button className={`desktop-nav-link ${showJoin ? 'active' : ''}`} onClick={openJoin}>🔗 Unirse</button>
            {showJoin && (
              <div className="join-dropdown">
                <p className="join-dropdown-title">Unirse a vaquita</p>
                <input
                  className="join-dropdown-input"
                  type="text"
                  placeholder="Código de invitación"
                  value={joinCode}
                  onChange={e => setJoinCode(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && handleJoin()}
                  autoFocus
                />
                <button className="join-dropdown-btn" onClick={handleJoin}>Unirme</button>
              </div>
            )}
          </div>
          <NavLink to="/my-vaquitas" className={({ isActive }) => `desktop-nav-link ${isActive ? 'active' : ''}`}>🐮 Vaquitas</NavLink>
          <NavLink to="/friends" className={({ isActive }) => `desktop-nav-link ${isActive ? 'active' : ''}`}>👥 Amigos</NavLink>
          <NavLink to="/profile" className={({ isActive }) => `desktop-nav-link ${isActive ? 'active' : ''}`}>👤 Perfil</NavLink>
        </nav>
      )}

      <div className="header-right">
        <button 
          className="theme-toggle"
          onClick={toggleTheme}
          title={isDarkMode ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
        >
          {isDarkMode ? '☀️' : '🌙'}
        </button>

        {isLoggedIn && <NotificationBell />}

        {isLoggedIn && (
          <div className="user-menu-container">
            <button
              className="user-button"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <CowAvatar seed={avatarSeed} size={28} />
              <span className="user-name">{userName}</span>
            </button>

            {showUserMenu && (
              <div className="user-menu">
                <Link to="/profile" className="menu-item" onClick={() => setShowUserMenu(false)}>
                  <span className="menu-icon">👤</span>
                  Mi Perfil
                </Link>
                <Link to="/my-vaquitas" className="menu-item" onClick={() => setShowUserMenu(false)}>
                  <span className="menu-icon">🐮</span>
                  Mis Vaquitas
                </Link>
                <Link to="/create-vaquita" className="menu-item" onClick={() => setShowUserMenu(false)}>
                  <span className="menu-icon">➕</span>
                  Crear Vaquita
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="menu-item" onClick={() => setShowUserMenu(false)}>
                    <span className="menu-icon">🛠</span>
                    Panel Admin
                  </Link>
                )}
                <button onClick={handleLogout} className="menu-item logout">
                  <span className="menu-icon">🚪</span>
                  Cerrar Sesión
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <style jsx>{`
        .header {
          background: var(--bg-secondary);
          padding: 0.75rem 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 4px var(--shadow-color);
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          max-width: 100vw;
        }

        .desktop-nav {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .desktop-nav-link {
          padding: 0.4rem 0.85rem;
          border-radius: 8px;
          color: var(--text-muted);
          text-decoration: none;
          font-size: 0.92rem;
          font-weight: 500;
          transition: background 0.15s, color 0.15s;
          white-space: nowrap;
        }

        .desktop-nav-link:hover {
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .desktop-nav-link.active {
          color: var(--accent-color);
          background: var(--bg-primary);
        }

        button.desktop-nav-link {
          background: none;
          border: none;
          cursor: pointer;
          font-family: inherit;
        }

        @media (max-width: 768px) {
          .desktop-nav { display: none; }
        }

        .join-dropdown-wrap {
          position: relative;
        }

        .join-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          width: 260px;
          background: var(--bg-secondary);
          border-radius: 12px;
          box-shadow: 0 8px 32px var(--shadow-color);
          border: 1px solid var(--border-color);
          z-index: 2000;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .join-dropdown-title {
          margin: 0;
          font-size: 0.85rem;
          font-weight: 700;
          color: var(--text-primary);
        }

        .join-dropdown-input {
          padding: 0.65rem 0.85rem;
          border: 1.5px solid var(--border-color);
          border-radius: 8px;
          font-size: 0.95rem;
          background: var(--bg-primary);
          color: var(--text-primary);
          text-transform: uppercase;
          letter-spacing: 0.08em;
          outline: none;
          width: 100%;
          box-sizing: border-box;
        }

        .join-dropdown-input:focus { border-color: var(--accent-color); }

        .join-dropdown-btn {
          padding: 0.6rem;
          background: var(--accent-color);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.92rem;
          font-weight: 600;
          cursor: pointer;
          width: 100%;
        }

        .join-dropdown-btn:hover { opacity: 0.9; }

        .header-right {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .theme-toggle {
          background: none;
          border: none;
          font-size: 1.2rem;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
        }

        .theme-toggle:hover {
          background: var(--bg-primary);
        }

        .logo {
          color: var(--text-primary);
          font-size: 1.2rem;
          font-weight: bold;
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          white-space: nowrap;
        }

        .user-menu-container {
          position: relative;
        }

        .user-button {
          background: none;
          border: none;
          padding: 0.5rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-size: 1rem;
          color: var(--text-secondary);
          border-radius: 4px;
          transition: background 0.2s;
        }

        .user-button:hover {
          background: var(--bg-primary);
        }

        @media (max-width: 768px) {
          .user-name { display: none; }
        }

        .user-icon {
          font-size: 1.2rem;
        }

        .user-menu {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 0.5rem;
          background: var(--bg-secondary);
          border-radius: 8px;
          box-shadow: 0 4px 6px var(--shadow-color);
          min-width: 200px;
          overflow: hidden;
        }

        .menu-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          color: var(--text-secondary);
          text-decoration: none;
          transition: background 0.2s;
          border: none;
          width: 100%;
          text-align: left;
          font-size: 1rem;
          background: none;
          cursor: pointer;
        }

        .menu-item:hover {
          background: var(--bg-primary);
        }

        .menu-icon {
          font-size: 1.2rem;
        }

        .logout {
          border-top: 1px solid var(--border-color);
          color: var(--danger-color);
        }

        .logout:hover {
          background: var(--danger-bg);
        }
      `}</style>
    </header>
  );
} 